import React, { useEffect, useState } from 'react';
import { useFeathers, isPermitted } from '../../app/util';
import { useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { DataDetailToolbar, LoadingSpinner, PaginationControl, PaginationStatus } from '../../components';
import moment from 'moment';
import { Add as AddIcon } from '@mui/icons-material';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { MeasureList } from '../../app/feathers/measure-lists';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 16px;

  h1 {
    font-size: 28px;
    margin: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 8px;
  border: 1px #EEEEEE solid;
  overflow: hidden;
  background: #FFFFFF;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 80px;
  grid-gap: 24px;
  padding: 16px 24px;
  background: #F7F9FE;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;

  h5 {
    margin: 0;
    font-weight: bold;
    color: #313639;
  }
`;

const TableRow = styled.div<{ onClick?: () => void }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 80px;
  grid-gap: 24px;
  padding: 16px 24px;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.onClick ? '#F7F9FE' : 'transparent'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    margin-bottom: 20px;
  }
`;

const StatusChip = styled(Chip)<{ status: string }>`
  background-color: ${props => {
    switch (props.status) {
      case 'started':
        return '#FFF3E0';
      case 'labeled':
        return '#FFF3E0';
      case 'matched':
        return '#FFF3E0';
      case 'created':
        return '#E8F5E8';
      default:
        return '#F5F5F5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'started':
        return '#E65100';
      case 'labeled':
        return '#2E7D32';
      case 'matched':
        return '#7B1FA2';
      case 'created':
        return '#FFFFFF';
      default:
        return '#757575';
    }
  }};
  font-weight: bold;
`;

const MeasureLists = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const { key: electionKey } = useParams<{ key: string }>();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: any) => state.user);
  const electionData = useSelector((state: any) => state?.elections?.byKey[electionKey]);
  
  const [measureLists, setMeasureLists] = useState<MeasureList[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const canCreate = isPermitted(user, ['editor', 'admin', 'super-admin']);

  const getMeasureLists = async (page: number = 0) => {
    setLoading(true);
    try {
      const response = await feathers.getService('measure-lists').find({
        query: {
          election: electionKey,
          $skip: page * itemsPerPage,
          $limit: itemsPerPage,
          $sort: { createdAt: -1 }
        }
      });
      setMeasureLists(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching measure lists:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (feathers && electionKey) {
      getMeasureLists(currentPage);
    }
  }, [feathers, electionKey, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  const handleRowClick = (list: MeasureList) => {
    history.push(`/elections/${electionKey}/measure-lists/${list._id}`);
  };

  const handleCreateNew = () => {
    history.push(`/elections/${electionKey}/measure-lists/new`);
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'started':
        return 'Uploaded';
      case 'labeled':
        return 'Labeled';
      case 'matched':
        return 'Matched';
      case 'created':
        return 'Created';
      default:
        return status;
    }
  };

  if (!electionData) {
    return (
      <Wrapper>
        <LoadingTile>
          <span>Loading election data...</span>
          <LoadingSpinner width={64} />
        </LoadingTile>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` }}}
        navTree={[
          { text: 'Elections', to: { pathname: `/elections` } },
          { text: electionData?.name || 'Election', to: { pathname: `/elections/${electionKey}` } },
          { text: 'Measure Lists' }
        ]}
        actionButtonsComponent={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              style={{ backgroundColor: '#22034F', color: '#FFFFFF' }}
            >
              New Upload
            </Button>
          ) : null
        }
      />
      <Content>
        <TableHeader>
          <Typography variant="h5">Source</Typography>
          <Typography variant="h5">Status</Typography>
          <Typography variant="h5">Created</Typography>
          <Typography variant="h5">Updated</Typography>
          <Typography variant="h5">Actions</Typography>
        </TableHeader>

        {loading ? (
          <LoadingTile>
            <span>Loading measure lists...</span>
            <LoadingSpinner width={64} />
          </LoadingTile>
        ) : measureLists.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No measure lists found for this election.
            </Typography>
            {canCreate && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                style={{ marginTop: '16px' }}
              >
                Upload First List
              </Button>
            )}
          </div>
        ) : (
          measureLists.map((list) => (
            <TableRow
              key={list._id}
              onClick={() => handleRowClick(list)}
            >
              <div>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  {list.source}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {list.state}
                </Typography>
              </div>
              
              <StatusChip
                label={getStatusDisplayText(list.status)}
                status={list.status}
                size="small"
              />
              
              <Typography variant="body2">
                {moment(list.createdAt).format('MMM D, YYYY')}
              </Typography>
              
              <Typography variant="body2">
                {moment(list.updatedAt).format('MMM D, YYYY')}
              </Typography>
              
              <div>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(list);
                  }}
                >
                  View
                </Button>
              </div>
            </TableRow>
          ))
        )}
      </Content>

      {totalCount > itemsPerPage && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '24px' 
        }}>
          <PaginationStatus
            activePage={currentPage + 1}
            items={totalCount}
            itemsPerPage={itemsPerPage}
            itemName="measure list"
          />
          <PaginationControl
            activePage={currentPage + 1}
            items={totalCount}
            itemsPerPage={itemsPerPage}
            onChange={handlePageChange}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default MeasureLists;


